import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { StarIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { useQuery, useMutation, usePagination } from '../../hooks';
import { reviewsAPI } from '../../services/api';
import { selectUser } from '../../store/slices/authSlice';
import {
  Card, SectionHeader, Button, Badge,
  Pagination, LoadingPage, EmptyState, Tabs,
} from '../../components/common/UI';
import { format } from 'date-fns';

// ── Star rating display ───────────────────────────────
const StarRating = ({ rating = 0 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) =>
      s <= rating
        ? <StarSolid key={s} className="h-3.5 w-3.5 text-warning-400" />
        : <StarIcon  key={s} className="h-3.5 w-3.5 text-surface-300" />
    )}
  </div>
);

const REVIEW_STATUS_COLOR = { pending: 'yellow', verified: 'green', rejected: 'red' };

const TABS = [
  { value: 'pending',  label: 'Pending' },
  { value: '',         label: 'All' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
];

// ── Main page ─────────────────────────────────────────
export default function BusinessReviews() {
  const user = useSelector(selectUser);
  const isAdmin = ['super_admin', 'admin', 'moderator'].includes(user?.role);

  const [tab,      setTab]    = useState('pending');
  const [actingId, setActing] = useState(null);
  const { page, setPage, reset } = usePagination();

  const { data: reviewsData, loading, refetch } = useQuery(
    () => reviewsAPI.businessList({ status: tab || undefined, page, limit: 15 }),
    [tab, page],
  );

  const reviews = reviewsData?.rows || reviewsData?.reviews || reviewsData?.data?.rows || reviewsData?.data?.reviews || (Array.isArray(reviewsData) ? reviewsData : []);
  const total   = reviewsData?.count || reviewsData?.total || reviewsData?.data?.count || reviewsData?.data?.total || reviews.length || 0;
  const pages   = Math.ceil(total / 15) || 1;

  // ── Verify / Reject ──────────────────────────────
  const { mutate: act, loading: acting } = useMutation(
    ({ reviewId, status }) => reviewsAPI.verify(reviewId, { status }),
    {
      onSuccess: (res, vars) => {
        const targetStatus = vars?.status || res?.status || 'verified';
        toast.success(targetStatus === 'verified' ? 'Review verified ✓' : 'Review rejected');
        setActing(null);
        refetch();
      },
    }
  );

  const handleAct = (reviewId, status) => {
    setActing(reviewId + status);
    act({ reviewId, status });
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title={isAdmin ? "Customer Reviews (All Businesses)" : "Customer Reviews"}
        subtitle={isAdmin ? "Moderate reviews across all businesses on the platform" : "Verify reviews before they appear on your product pages"}
      />

      <Tabs tabs={TABS} active={tab} onChange={(v) => { setTab(v); reset(); }} />

      <Card padding={false}>
        {loading ? <LoadingPage /> : reviews.length === 0 ? (
          <EmptyState
            icon={StarIcon}
            title="No reviews found"
            description={tab === 'pending' ? 'No pending reviews to verify right now.' : `No ${tab || ''} reviews yet.`}
          />
        ) : (
          <div className="divide-y divide-surface-100">
            {reviews.map((review) => {
              const id         = review.id || review._id;
              const isPending  = !review.status || review.status === 'pending';
              const isVerified = review.status === 'verified';
              const isRejected = review.status === 'rejected';

              return (
                <div key={id} className="flex gap-4 p-5 hover:bg-surface-50 transition-colors">

                  {/* Product thumbnail */}
                  <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl border border-surface-200 bg-surface-100">
                    {review.product?.images?.[0] || review.product_image ? (
                      <img
                        src={review.product?.images?.[0] || review.product_image}
                        alt={review.product?.name || review.product_name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-surface-300">
                        <StarIcon className="h-6 w-6" />
                      </div>
                    )}
                  </div>

                  {/* Review body */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide truncate max-w-[220px]">
                          {review.product?.name || review.product_name || '—'}
                        </p>
                        <div className="mt-0.5 flex items-center gap-2">
                          <StarRating rating={review.rating} />
                          <span className="text-xs text-surface-400">{review.rating}/5</span>
                        </div>
                      </div>
                      <Badge variant={REVIEW_STATUS_COLOR[review.status] || 'yellow'} dot>
                        {(review.status || 'pending').replace(/_/g, ' ')}
                      </Badge>
                    </div>

                    {review.title && (
                      <p className="mt-1.5 text-sm font-semibold text-surface-800">{review.title}</p>
                    )}
                    <p className="mt-1 text-sm text-surface-600 leading-relaxed line-clamp-3">
                      {review.comment || review.body || review.text || '(No comment)'}
                    </p>

                    <div className="mt-2 flex items-center gap-3 text-xs text-surface-400 flex-wrap">
                      <span>
                        By <span className="font-medium text-surface-600">
                          {review.user?.name || `${review.user?.first_name || ''} ${review.user?.last_name || ''}`.trim() || review.user_name || review.customer_name || 'Customer'}
                        </span>
                      </span>
                      {review.business_name && (
                        <span className="inline-flex items-center gap-1 font-medium text-primary-700 bg-primary-50 px-2 py-0.5 rounded-md border border-primary-200">
                          🏪 {review.business_name}
                        </span>
                      )}
                      {review.created_at && (
                        <span>· {format(new Date(review.created_at), 'dd MMM yyyy')}</span>
                      )}
                      {review.is_verified_purchase && (
                        <span className="text-success-600 font-medium">· ✓ Verified Purchase</span>
                      )}
                    </div>

                    {/* Action buttons — all statuses can be changed */}
                    <div className="mt-3 flex items-center flex-wrap gap-2">
                      {/* Pending: show both Verify & Reject */}
                      {isPending && (
                        <>
                          <Button
                            size="sm"
                            variant="primary"
                            icon={CheckCircleIcon}
                            loading={acting && actingId === id + 'verified'}
                            disabled={acting}
                            onClick={() => handleAct(id, 'verified')}
                            id={`verify-btn-${id}`}
                          >
                            Verify
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            icon={XCircleIcon}
                            loading={acting && actingId === id + 'rejected'}
                            disabled={acting}
                            onClick={() => handleAct(id, 'rejected')}
                            id={`reject-btn-${id}`}
                          >
                            Reject
                          </Button>
                        </>
                      )}

                      {/* Verified: show Reject button + status label */}
                      {isVerified && (
                        <>
                          <p className="flex items-center gap-1 text-xs font-medium text-success-600">
                            <CheckCircleIcon className="h-3.5 w-3.5" /> Verified — visible to customers
                          </p>
                          <Button
                            size="sm"
                            variant="danger"
                            icon={XCircleIcon}
                            loading={acting && actingId === id + 'rejected'}
                            disabled={acting}
                            onClick={() => handleAct(id, 'rejected')}
                            id={`reject-verified-btn-${id}`}
                          >
                            Reject
                          </Button>
                        </>
                      )}

                      {/* Rejected: show Re-verify button + status label */}
                      {isRejected && (
                        <>
                          <p className="flex items-center gap-1 text-xs font-medium text-danger-500">
                            <XCircleIcon className="h-3.5 w-3.5" /> Rejected — hidden from customers
                          </p>
                          <Button
                            size="sm"
                            variant="primary"
                            icon={CheckCircleIcon}
                            loading={acting && actingId === id + 'verified'}
                            disabled={acting}
                            onClick={() => handleAct(id, 'verified')}
                            id={`re-verify-btn-${id}`}
                          >
                            Re-verify
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="px-5 pb-2">
              <Pagination page={page} pages={pages} onPageChange={setPage} />
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
